/* @flow */

// eslint-disable-next-line import/no-cycle
import dispatcher from './dispatcher';

type Payload = { type: string, payload: any[] };

// internal introspection tool
const getFunctionsOf = (target: Object): Array<string> => {
  const props = [];
  let obj = target;

  // inheritance tree
  do {
    props.push(...Object.getOwnPropertyNames(obj));
    // eslint-disable-next-line no-cond-assign
  } while ((obj = Object.getPrototypeOf(obj)));

  return props
    .sort()
    .filter((e, i, a) => a.indexOf(e) === i)
    .filter(e => typeof target[e] === 'function');
};

/**
 * StateStore super class
 * Works with @reducer decorator in order to simplify definition of domain stores
 */
export default class StateStore {
  _name: string;

  _reducers: Map<string, Object> = new Map();

  _effects: Map<string, Object> = new Map();

  dispatchToken: string;

  constructor(name?: string, secured: boolean = true) {
    this._name = name || this.constructor.name;

    const methods = getFunctionsOf(this);

    // adding reducers
    methods
      // $FlowFixMe: proper type definition for this[name]
      .map(action => this[action]._reducer)
      .filter(Boolean)
      .forEach(({ key, fn }) =>
        this._reducers.set(
          // global, fallback to local
          key.includes('.') ? key : `${this._name}.${key}`,
          fn.bind(this),
        ),
      );

    // adding effects
    methods
      // $FlowFixMe: proper type definition for this[name]
      .map(action => this[action]._effect)
      .filter(Boolean)
      .forEach(({ key, fn }) => this._effects.set(key, fn.bind(this)));

    this.dispatchToken = dispatcher.register(this, secured);
  }

  /*
   * State reducer that takes in an external action, and derives a new state using
   * that new action and the existing state.
   *
   * This is a helper method that will automatically call the correct class instance
   * methods based on the action type string
   */
  reduce(action: Payload): void {
    const { type, payload } = action;

    if (!type) {
      throw new Error('Action dispatched without a type');
    }

    if (this._reducers.has(type)) {
      const method = this._reducers.get(type);
      return method ? method.apply(this, payload) : undefined;
    }

    return undefined;
  }
}
